import React, {useCallback, useMemo, useState} from 'react';
import {
    NavLink,
    Outlet, useLocation,
    useMatches,
    useNavigate,
    useOutletContext, useRevalidator,
    useRouteLoaderData
} from 'react-router-dom';


import styles from '../../Info.module.scss';

import {Button} from '@progress/kendo-react-buttons';
import {Form, FormRenderProps} from '@progress/kendo-react-form';

import {caretAltLeftIcon} from "@progress/kendo-svg-icons";
import {SvgIcon} from "@progress/kendo-react-common";
import assetsIcon from "../../../assets/assetsIcon.svg"
import detailsIcon from "../../../assets/detailsIcon.svg"
import {
    IConnectedService,
    ILocation,
    ILookupValue,
    LocationType
} from "../../../model";
import {camerasService, locationsService, notifications} from "../../../services";
import {ICustomerCameraFilter} from "../../../model/api/customers/customer-camera-filter";
import {useConfirmation} from "../../../components/common";
import {usePermissions} from "../../../components/auth/PermissionsProvider";

interface INavLink {
    name: string,
    svgIcon: any,
}

interface RouteHandle {
    tabName?: string;
    crumb?: (data: any) => string;
}

export const LocationInfo = () => {
    const { hasEditAccess } = usePermissions();
    const locationFromRoute = useRouteLoaderData('location-info') as ILocation;
    const matches = useMatches();
    const revalidator = useRevalidator();
    const currentTabMatch = matches[matches.length - 1];
    const handle = currentTabMatch?.handle as RouteHandle | undefined;
    const currentTab = handle?.tabName ?? 'Details';
    const navigate = useNavigate();
    const route = useLocation();
    const initialCustomer = route.state?.customer as ILookupValue | undefined;
    const state = route.state as { edit: boolean } | null;
    const [saving, setSaving] = useState(false);
    const confirmation = useConfirmation();
    const [activeTab, setActiveTab] = useState(currentTab);
    const [location, setLocation] = useState<ILocation>(locationFromRoute);
    const [originalLocation, setOriginalLocation] = useState<ILocation | null>(null);
    const [edit, setEdit] = useState(state ? state.edit : false);
    const isNew = locationFromRoute.id === 0;
    const [isFormModified, setIsFormModified] = useState(false);
    const [collapsed, setCollapsed] = useState(false);

    const mainNavLinks: INavLink[] = [
        {name: 'Details', svgIcon: detailsIcon},
        {name: 'Assets', svgIcon: assetsIcon},
    ];

    const handleEditClick = () => {
        setOriginalLocation(location);
        setEdit(true);
    };

    const handleCancelForm = () => {
        if (isNew) {
            navigate('/locations');
        } else {
            if (originalLocation) {
                setLocation(originalLocation);
            }
            setIsFormModified(false);
            setEdit(false);
        }
    }

    const handleDelete = (location: ILocation) => {
        if (location.connectedServices.length > 0) {
            notifications.error(
                'Connected services found. This location cannot be deleted until all connected services and cameras are removed or deactivated.',
            );
            return;
        } else {
            const filter: ICustomerCameraFilter = { customerId: location?.customer?.uniqueId ?? '', locationId: location.uniqueId };
            let camerasCount = 0;
            camerasService.getCustomerCamerasList(filter).then((camerasData) => {
                camerasCount = camerasData.length;
                if (camerasCount > 0) {
                    notifications.error(
                        'Cameras found. This location cannot be deleted until all connected services and cameras are removed or deactivated.',
                    );
                    return;
                }

                confirmation({
                    message: `Are you sure you want to remove location '${location.name}'? This action cannot be undone.`,
                })
                    .then(() => locationsService.remove([location.id]))
                    .then(() => {
                        notifications.success(`Location '${location.name}' successfully removed.`);
                        navigate('/locations');
                    })
                    .catch((err) => {});
            });
        }
    };

    const handleFormSubmit = useCallback(
        async (dataItem: { [name: string]: any }) => {
            dataItem['connectedServiceIds'] = (dataItem['connectedServices'] as ILookupValue[])
                ?.filter((x) => x?.id !== undefined)
                ?.map((x) => x.id);

            const moduleIds = (dataItem['connectedServices'] as IConnectedService[])
                ?.filter((x) => x?.modules?.length > 0)
                ?.map((x) => x.modules.map((y) => y.id));

            dataItem['moduleIds'] = moduleIds?.length ? moduleIds.reduce((x, y) => x.concat(y)) : [];

            const upsert = {
                ...(dataItem as any),
                customerId: dataItem.customer ? dataItem.customer.id : '',
                settings: dataItem.type === LocationType.Garage ? dataItem.settings : undefined,
            } as any;

            try {
                setSaving(true);
                if (location?.id) {
                    await locationsService.update(location.id, upsert).then((data) => {
                        notifications.success('Location updated');
                        const newLocationData = {
                            ...upsert,
                            id: location.id,
                        } as ILocation;
                        setLocation(newLocationData);
                        revalidator.revalidate();
                    });
                } else {
                    await locationsService.create(upsert).then((newId) => {
                        notifications.success('Location created');
                        const newLocationData = {
                            ...dataItem,
                            id: newId,
                        } as ILocation;
                        setLocation(newLocationData);
                        navigate(`/locations/${newId}/details`);
                    });

                }
            } finally {
                setSaving(false);
                setEdit(false);
            }
        },
        [location, navigate],
    );


    const content = useMemo(() => {
        return (
        <div className={styles.newInfoPage}>
            <nav className={`${styles.mainSidebar} ${collapsed ? styles.collapsed : ''}`}>
                <button
                    type="button"
                    className={styles.sidebarToggle}
                    onClick={() => setCollapsed(!collapsed)}
                    title={collapsed ? "Expand" : "Collapse"}
                >
                    <div style={{ transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)', display: 'flex' }}>
                        <SvgIcon icon={caretAltLeftIcon} />
                    </div>
                </button>
                <ul>
                    {mainNavLinks.map((link) => (
                        <li key={link.name}>
                            {location.id ? <NavLink
                                    onClick={() => setActiveTab(link.name)}
                                    to={`/locations/${location?.id}/${link.name.toLowerCase()}`}
                                    className={({isActive}) => (isActive ? styles.active : '')}
                                    title={collapsed ? link.name : ''}
                                >
                                    <img src={link.svgIcon} alt={link.name}/>
                                    <span className={styles.linkText} style={{lineHeight: '18px'}}>{link.name}</span>
                                </NavLink>
                                :
                                <span className={link.name === 'Details' ? styles.inActiveSidebarDetailLink : styles.inActiveSidebarLink} >
                                    <img src={link.svgIcon} alt={link.name}/>
                                    <span style={{lineHeight: '18px'}}>{link.name}</span>
                                </span>
                            }
                        </li>
                    ))}
                </ul>
            </nav>

            <main className={styles.contentArea}>
                <header className={styles.contentHeader}>
                    <Button className={styles.crumbButtonLink} fillMode={"link"} onClick={() => navigate('/locations')}>
                        <SvgIcon icon={caretAltLeftIcon}/>
                        <span>Locations</span>
                    </Button>
                    {currentTab === 'Details' &&
                        <div className={styles.headerActions}>
                            {edit ? <div>
                                    <Button onClick={() => handleCancelForm()}>Cancel</Button>
                                    <Button type="submit"
                                            form="main-location-form"
                                            disabled={!isFormModified}
                                            themeColor={'primary'}>Save</Button>
                                </div>
                                :
                                hasEditAccess && <div>
                                    <Button onClick={() => handleEditClick()}>Edit</Button>
                                    {location ? <Button onClick={() => handleDelete(location)}>Delete</Button> : null}
                                </div>
                            }

                        </div>
                    }

                </header>

                <div className={styles.tabContentWrapper}>
                    <Form
                        onSubmit={handleFormSubmit}
                        key={`form-${location?.id}`}
                        initialValues={location ?? {
                            name: '',
                            type: LocationType.ParkingLot,
                            address: '',
                            connectedServices: [],
                            customer: initialCustomer,
                        }}
                        render={(formRenderProps: FormRenderProps) => {
                            if (formRenderProps.modified !== isFormModified) {
                                setIsFormModified(formRenderProps.modified);
                            }

                            return (
                                <form id="main-location-form" onSubmit={formRenderProps.onSubmit} className="k-form">
                                    <Outlet context={{formRenderProps, edit}}/>
                                </form>
                            );
                        }}
                    />

                </div>
            </main>
        </div>
    );
    }, [
        collapsed,
        location,
        edit,
        saving,
        currentTab,
        hasEditAccess,
        initialCustomer,
        handleFormSubmit,
        handleCancelForm,
        handleDelete,
        navigate,
        mainNavLinks
    ]);

    return content;
};

type FormOutletContext = {
    formRenderProps: any;
    edit: boolean;
};

export function useFormOutletContext() {
    return useOutletContext<FormOutletContext>();
}

export default LocationInfo;
